import { useState, useEffect, useCallback, useMemo } from "react"
import MuiSlider from "@mui/material/Slider"
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded"
import StopCircleRoundedIcon from "@mui/icons-material/StopCircleRounded"
import "./slider_dust.css"
import "leaflet/dist/leaflet.css"
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew"
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos"
function formatDateForGeoServer(date) {
  date.setMinutes(0, 0, 0, 0) // Establece minutos, segundos y milisegundos a cero
  return date.toISOString().replace(/:\d{2}\.\d{3}Z$/, ":00Z") // Retorna la fecha en formato ISO sin milisegundos
}

function TimeSliderDust({ onTimeChange, fetchGeoJSON, selectedCategory, forecastValue }) {
  const [isAutoPlay, setIsAutoPlay] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [adjustedDateTime, setAdjustedDateTime] = useState("")
  const [sliderThumbPosition, setSliderThumbPosition] = useState({ top: 0, left: 0 })
  const [isAlertVisible, setIsAlertVisible] = useState(true)
  const [windowWidth, setWindowWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1200)

  // 1) Calcula los offsets (0–48h) donde hay datos cada 6h
  const marks = useMemo(() => {
    const start = new Date(forecastValue)
    const offs = []

    // 🔹 Añadir manualmente el punto inicial +0 siempre
    offs.push({ value: 0, label: "+0" })

    // 🔹 Luego generar el resto de puntos cada 3 horas
    for (let i = 1; i <= 66; i++) {
      const d = new Date(start)
      d.setUTCHours(d.getUTCHours() + i)

      if (d.getUTCHours() % 3 === 0) {
        offs.push({
          value: i,
          label: `+${i}`,
        })
      }
    }

    return offs
  }, [forecastValue])

  // 2) define la función antes de usarla
  const calculateInitialSliderValue = useCallback(() => {
    return marks.length > 0 ? marks[0].value : 0
  }, [marks])

  // 3) inicializa el estado con función lazy para que sólo se llame una vez
  const [sliderValue, setSliderValue] = useState(() => calculateInitialSliderValue())

  // 4) cuando cambie forecastValue o marks, reinicia
  useEffect(() => {
    setSliderValue(calculateInitialSliderValue())
  }, [calculateInitialSliderValue, forecastValue])

  // Track window width for responsive adjustments
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    // Actualización basada en forecastValue
    const newSliderValue = calculateInitialSliderValue(forecastValue)
    setSliderValue(newSliderValue)
  }, [forecastValue])

  const handlePlayButtonClick = () => {
    setIsAutoPlay(!isAutoPlay)
    setIsPlaying(!isPlaying)
    setIsAlertVisible(true)
  }

  const handleCloseAlert = () => {
    setIsAlertVisible(false)
  }

  const playButtonStyle = {
    backgroundSize: "cover",
  }

  // Debounce function to prevent multiple calls
  const debounce = (func, wait) => {
    let timeout
    return function (...args) {

      clearTimeout(timeout)
      timeout = setTimeout(() => func.apply(this, args), wait)
    }
  }

  const debouncedFetchGeoJSON = useCallback(
    debounce((geoServerDateTime, category) => {
      fetchGeoJSON(geoServerDateTime, category)
    }, 500),
    [],
  )

  //detener autoplay si el slider es movido manualmente
  const handleChangeCommitted = () => {
    if (isAutoPlay) {
      setIsAutoPlay(false);   // detiene el autoplay
      setIsPlaying(false);    // cambia el icono a "play"
    }
  };

  const handleSliderChange = (e, value) => {
    setSliderValue(value);
    const dt = new Date(forecastValue);
    dt.setUTCHours(dt.getUTCHours() + value);
    const gs = formatDateForGeoServer(dt);
    setAdjustedDateTime(formatDateForDisplay(dt));
    onTimeChange(gs);
  };


  useEffect(() => {
    // Calculate slider thumb position
    if (document.querySelector(".horizontal-slider .thumb")) {
      const sliderThumb = document.querySelector(".horizontal-slider .thumb")
      const sliderRect = sliderThumb.getBoundingClientRect()
      setSliderThumbPosition({
        top: sliderRect.top - 500,
        left: sliderRect.left + sliderRect.width / 2,
      })
    }
  }, [sliderValue])

  function formatDateForDisplay(date) {
    const year = date.getUTCFullYear()
    const month = (date.getUTCMonth() + 1).toString().padStart(2, "0")
    const day = date.getUTCDate().toString().padStart(2, "0")
    const hours = date.getUTCHours().toString().padStart(2, "0")
    const minutes = date.getUTCMinutes().toString().padStart(2, "0")
    const seconds = date.getUTCSeconds().toString().padStart(2, "0")
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} UTC`
  }

  useEffect(() => {
    const forecastStartDate = new Date(forecastValue)
    forecastStartDate.setUTCHours(forecastStartDate.getUTCHours() + sliderValue)
    const geoServerDateTime = formatDateForGeoServer(forecastStartDate)
    onTimeChange(geoServerDateTime)
    setAdjustedDateTime(formatDateForDisplay(forecastStartDate))

    const timer = setTimeout(() => setIsAlertVisible(false), 5000)
    return () => clearTimeout(timer)
  }, [sliderValue, forecastValue])

  useEffect(() => {
    if (!isAutoPlay) return;
    const interval = setInterval(() => {
      setSliderValue(prev => {
        const idx = marks.findIndex(m => m.value === prev);
        const nextIdx = idx < 0 || idx === marks.length - 1 ? 0 : idx + 1;
        const nextVal = marks[nextIdx].value;
        const dt = new Date(forecastValue);
        dt.setUTCHours(dt.getUTCHours() + nextVal);
        onTimeChange(formatDateForGeoServer(dt));
        setAdjustedDateTime(formatDateForDisplay(dt));
        return nextVal;
      });
    }, 1300);
    return () => clearInterval(interval);
  }, [isAutoPlay, marks, forecastValue, onTimeChange]);
  const handleStepBack = () => {
    setSliderValue((prev) => Math.max(0, prev - 1))
    if (isAutoPlay) {
      setIsAutoPlay(false)
      setIsPlaying(false)
    }
  }

  const handleStepForward = () => {
    setSliderValue((prev) => Math.min(48, prev + 1))
    if (isAutoPlay) {
      setIsAutoPlay(false)
      setIsPlaying(false)
    }
  }
  // Determine slider width based on screen size
  const getSliderWidth = useMemo(() => {
    switch (true) {
      case windowWidth <= 275:
        return "50%"
      case windowWidth <= 500:
        return "70%"
      case windowWidth <= 700:
        return "77%"
      case windowWidth <= 900:
        return "84%"
      case windowWidth <= 1100:
        return "83%"
      case windowWidth <= 1500:
        return "88%"
      default:
        return "90%"
    }
  }, [windowWidth])

  //Para la altura de la barra del slider
  const getSliderTop = () => {
    if (windowWidth <= 700) return "61%";
    if (windowWidth <= 900) return "58%";
    if (windowWidth <= 1100) return "60%";
    if (windowWidth <= 1200) return "59%";
    if (windowWidth <= 1500) return "52%";
    return "50%";
  };


  // Determine font size for value label based on screen size
  const getValueLabelFontSize = useMemo(() => {
    switch (true) {
      case windowWidth <= 275:
        return "0.65rem";
      case windowWidth <= 500:
        return "0.7rem";
      case windowWidth <= 768:
        return "0.75rem";
      default:
        return "0.875rem";
    }
  }, [windowWidth]);

  // Determine padding for value label based on screen size
  const getValueLabelPadding = useMemo(() => {
    switch (true) {
      case windowWidth <= 275:
        return "0.25rem 0.5rem";
      case windowWidth <= 500:
        return "0.3rem 0.6rem";
      case windowWidth <= 768:
        return "0.4rem 0.8rem";
      default:
        return "0.5rem 1rem";
    }
  }, [windowWidth]);

  // Determine icon size based on screen size
  const getIconSize = useMemo(() => {
    switch (true) {
      case windowWidth <= 275:
        return 20;
      case windowWidth <= 500:
        return 22;
      case windowWidth <= 768:
        return 24;
      default:
        return 28;
    }
  }, [windowWidth]);

  //para definir la cercania de mark y mark-label
  const getMarkTop = useMemo(() => {
    switch (true) {
      case windowWidth <= 275:
        return "57%";
      case windowWidth <= 320:
        return "60%";
      case windowWidth <= 350:
        return "60%";
      case windowWidth <= 750:
        return "59%";
      case windowWidth <= 1000:
        return "59%";
      case windowWidth <= 1100:
        return "58%";
      case windowWidth <= 1200:
        return "59%";
      case windowWidth <= 1300:
        return "52%";
      case windowWidth <= 1500:
        return "52%";
      case windowWidth <= 1800:
        return "50%";
      default:
        return "50%";
    }
  }, [windowWidth]);

  const getMarkLabelTop = useMemo(() => {
    switch (true) {
      case windowWidth <= 275:
        return "26.5px";
      case windowWidth <= 320:
        return "30px";
      case windowWidth <= 350:
        return "30px";
      case windowWidth <= 750:
        return "35px";
      case windowWidth <= 900:
        return "28px";
      case windowWidth <= 1000:
        return "26px";
      case windowWidth <= 1099:
        return "30.5px";
      case windowWidth <= 1100:
        return "26.5px";

      case windowWidth <= 1200:
        return "-7.55px";
      case windowWidth <= 1300:
        return "-7.5px";
      case windowWidth <= 1500:
        return "-7.5px";
      case windowWidth <= 1800:
        return "-7.5px";
      default:
        return "-7.5px";
    }
  }, [windowWidth]);

  // Determine if marks should be shown based on screen size
  const shouldShowMarks = () => {
    return true;
  }

  return (
    <div className="time-slider-container">
      {/* Play/Stop Button */}
      <button
        className={`ts-custom-btn ${isAutoPlay ? 'ts-custom-btn--stop' : 'ts-custom-btn--play'}`}
        onClick={handlePlayButtonClick}
        aria-label={isAutoPlay ? "Stop autoplay" : "Start autoplay"}
      >
        {isAutoPlay ? (
          <StopCircleRoundedIcon />
        ) : (
          <PlayCircleOutlineRoundedIcon />
        )}
      </button>
      {/* Step Back Arrow (Mobile only) */}
      {windowWidth <= 768 && (
        <button
          className="ts-nav-btn"
          onClick={handleStepBack}
          aria-label="Step back 1 hour"
        >
          <ArrowBackIosNewIcon />
        </button>
      )}
      <MuiSlider
        className="time-slider-horizontal"
        min={marks[0]?.value ?? 0}
        max={marks[marks.length - 1]?.value ?? 66}
        value={sliderValue}
        step={null}
        disabled={false}
        onChange={handleSliderChange}
        onChangeCommitted={handleChangeCommitted}
        zIndex={1000}
        marks={marks}
        valueLabelDisplay="on"
        valueLabelFormat={(value) => {
          const d = new Date(forecastValue);
          d.setUTCHours(d.getUTCHours() + value);
          return formatDateForDisplay(d);
        }}
        sx={{
          // Only essential overrides that can't be handled by CSS
          '& .MuiSlider-valueLabel': {
            fontSize: windowWidth <= 350 ? '0.6rem' :
              windowWidth <= 500 ? '0.65rem' :
                windowWidth <= 768 ? '0.7rem' : '0.75rem',
          },
          '& .MuiSlider-markLabel': {
            fontSize: windowWidth <= 350 ? '0.6rem' :
              windowWidth <= 500 ? '0.65rem' :
                windowWidth <= 768 ? '0.7rem' : '0.75rem',
          }
        }}
      />
      <button className="custom-btn" style={playButtonStyle} onClick={handlePlayButtonClick}></button>
      {/* Step Forward Arrow (Mobile only) */}
      {windowWidth <= 768 && (
        <button
          className="ts-nav-btn"
          onClick={handleStepForward}
          aria-label="Step forward 1 hour"
        >
          <ArrowForwardIosIcon />
        </button>
      )}
    </div>
  )
}

export default TimeSliderDust

